import { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  FileText,
  CheckCircle2,
  Building2,
  ArrowRight,
  BookOpen,
  Mail,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const services = [
  {
    Icon: ShieldCheck,
    title: "NIS-2 Readiness Assessment",
    text: "Standortbestimmung Ihrer Liegenschaft oder Organisation: Betroffenheit, Reifegrad und Handlungsfelder nach NIS-2 und IEC 62443, inklusive Kurzbericht.",
  },
  {
    Icon: FileText,
    title: "Mapping NIS-2 ↔ IEC 62443 ↔ BSI",
    text: "Übersetzung der abstrakten NIS-2 Pflichten in konkrete Maßnahmen nach IEC 62443 und BSI Grundschutz Infrastrukturbausteine INF.13 und INF.14.",
  },
  {
    Icon: Building2,
    title: "OT-Security für Gebäudeautomation",
    text: "Umsetzung in bestehenden GA-Liegenschaften: Assetmanagement, Netzwerkarchitektur, Endpunkt-Absicherung und Betriebsmodell.",
  },
  {
    Icon: BookOpen,
    title: "Workbook & 60 Min. Sparring",
    text: "Strukturierter Leitfaden mit Self-Assessment plus 60 Minuten Online Assessment mit Tim Bauer, um schnell in Handlung zu kommen.",
  },
];

const scope = [
  "Sind wir laut NIS-2 überhaupt betroffen (wesentlich, wichtig, Lieferkette)?",
  "Welche technischen und organisatorischen Mindestmaßnahmen gelten für uns?",
  "Wie belegen wir Governance, Risikomanagement und Meldepflichten?",
  "Wie sichern wir Gebäudeautomation, OT-Netzwerke und Fernzugriffe ab?",
  "Wie strukturieren wir Verantwortlichkeiten zwischen IT, OT, Facility und Geschäftsleitung?",
  "Wie priorisieren wir Quick Wins vor größeren Programm-Themen?",
];

const faqs = [
  {
    q: "Was ist die NIS-2 Richtlinie?",
    a: "NIS-2 ist die überarbeitete EU-Richtlinie zur Netz- und Informationssicherheit. Sie erweitert den Kreis der verpflichteten Unternehmen und Einrichtungen deutlich, verschärft die Anforderungen an Risikomanagement, Meldepflichten und Governance und adressiert ausdrücklich auch die Lieferkette.",
  },
  {
    q: "Ist mein Unternehmen von NIS-2 betroffen?",
    a: "Betroffen sind wesentliche und wichtige Einrichtungen aus 18 Sektoren, unter anderem Energie, Verkehr, Gesundheit, digitale Infrastruktur, öffentliche Verwaltung sowie das produzierende Gewerbe ab bestimmten Größenklassen. Auch Betreiber großer Liegenschaften und Zulieferer solcher Einrichtungen können in den Anwendungsbereich fallen. Im Assessment klären wir das für Ihren Fall konkret.",
  },
  {
    q: "Was hat NIS-2 mit Gebäudeautomation und OT zu tun?",
    a: "NIS-2 verlangt, dass Netz- und Informationssysteme sicher betrieben werden. In modernen Liegenschaften gehören dazu auch Gebäudeautomation, OT-Netzwerke und Fernwartungszugänge. Ohne ein sauberes Assetmanagement, eine belastbare Netzwerkarchitektur und ein OT-Betriebsmodell nach IEC 62443 lassen sich die NIS-2 Anforderungen dort nicht wirtschaftlich erfüllen.",
  },
  {
    q: "Wie hängt NIS-2 mit IEC 62443 und BSI Grundschutz zusammen?",
    a: "NIS-2 formuliert die Pflichten auf einer hohen Ebene, IEC 62443 und die BSI Infrastrukturbausteine INF.13 und INF.14 liefern die konkreten technischen und organisatorischen Maßnahmen. Ich mappe beide Welten, damit aus abstrakten Anforderungen ein umsetzbares Betriebsmodell wird.",
  },
  {
    q: "Was passiert, wenn NIS-2 nicht umgesetzt wird?",
    a: "Neben Bußgeldern und aufsichtsrechtlichen Maßnahmen trifft NIS-2 auch die persönliche Verantwortung der Geschäftsleitung. Wichtiger für den Alltag: Ohne ein tragfähiges OT-Security-Modell steigen Ausfall- und Manipulationsrisiken im Betrieb spürbar.",
  },
  {
    q: "Wie starten wir am besten?",
    a: "Der pragmatische Einstieg ist das FACHSPRECHPARTNER Workbook mit Self-Assessment und 60 Minuten Online Assessment. Damit haben Sie in kurzer Zeit einen priorisierten Fahrplan, den Sie intern und mit Dienstleistern weiter bearbeiten können.",
  },
];

const Nis2 = () => {
  const openContact = () =>
    window.dispatchEvent(new CustomEvent("open-contact-form"));

  const canonical = "https://fachsprechpartner.de/nis2";

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        name: "NIS-2 Beratung für Gebäudeautomation und OT",
        serviceType: "OT-Security und NIS-2 Beratung",
        provider: {
          "@type": "Organization",
          name: "FACHSPRECHPARTNER",
          url: "https://fachsprechpartner.de",
        },
        areaServed: { "@type": "Country", name: "Deutschland" },
        description:
          "Beratung zur NIS-2 Richtlinie mit Fokus auf Gebäudeautomation und OT: Betroffenheit, Readiness Assessment, Mapping zu IEC 62443 und BSI INF.13/INF.14 und Umsetzung im Betrieb.",
        url: canonical,
      },
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://fachsprechpartner.de/" },
          { "@type": "ListItem", position: 2, name: "NIS-2", item: canonical },
        ],
      },
    ],
  };

  return (
    <>
      <Helmet>
        <title>NIS-2 Richtlinie: Beratung für Gebäudeautomation & OT</title>
        <meta
          name="description"
          content="NIS-2 pragmatisch umsetzen: Betroffenheit prüfen, IEC 62443 und BSI INF.13/INF.14 mappen und Gebäudeautomation sowie OT absichern. Assessment und Workbook."
        />
        <meta name="keywords" content="NIS2, NIS-2 Richtlinie, NIS-2 Umsetzung, NIS-2 Beratung, NIS-2 Gebäudeautomation, OT-Security, IEC 62443" />
        <link rel="canonical" href={canonical} />
        <link rel="alternate" hrefLang="de" href={canonical} />
        <link rel="alternate" hrefLang="x-default" href={canonical} />
        <meta property="og:title" content="NIS-2 Richtlinie: Beratung für Gebäudeautomation & OT" />
        <meta
          property="og:description"
          content="NIS-2 pragmatisch umsetzen: Betroffenheit prüfen, IEC 62443 und BSI INF.13/INF.14 mappen und Gebäudeautomation sowie OT absichern."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content="https://fachsprechpartner.de/og-image.webp" />
        <meta property="og:image:type" content="image/webp" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="FACHSPRECHPARTNER Tim Bauer, OT-Security und Gebäudeautomation" />
        <meta property="og:locale" content="de_DE" />
        <meta property="og:site_name" content="Fachsprechpartner" />
        <meta name="twitter:title" content="NIS-2 Richtlinie: Beratung für Gebäudeautomation & OT" />
        <meta name="twitter:description" content="NIS-2 pragmatisch umsetzen: Betroffenheit prüfen, IEC 62443 und BSI INF.13/INF.14 mappen und Gebäudeautomation sowie OT absichern." />
        <meta name="twitter:image" content="https://fachsprechpartner.de/og-image.webp" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <Navbar />

      <main className="min-h-screen bg-background">
        {/* Hero */}
        <section className="pt-32 pb-16 md:pt-40 md:pb-24 bg-gradient-to-b from-primary to-primary/90 text-primary-foreground">
          <div className="max-w-5xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/20 text-accent text-xs font-semibold uppercase tracking-wider mb-6">
                <ShieldCheck className="w-4 h-4" />
                NIS-2 Richtlinie
              </div>
              <h1 className="text-4xl md:text-6xl font-heading font-bold leading-tight mb-6">
                NIS-2 pragmatisch umsetzen, statt Papier zu produzieren.
              </h1>
              <p className="text-lg md:text-xl text-primary-foreground/85 mb-8 leading-relaxed max-w-3xl">
                Ich übersetze die NIS-2 Richtlinie in ein Betriebsmodell für Ihre
                Gebäudeautomation und OT, mit klarem Mapping zu IEC 62443 und
                BSI Grundschutz INF.13 und INF.14. Fokus: Wirkung im Betrieb,
                nicht Ordner im Regal.
              </p>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={openContact}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-accent text-accent-foreground font-semibold hover:bg-accent/90 transition-colors"
                >
                  Assessment anfragen <ArrowRight className="w-4 h-4" />
                </button>
                <Link
                  to="/workbook"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-primary-foreground/30 text-primary-foreground font-semibold hover:bg-primary-foreground/10 transition-colors"
                >
                  <BookOpen className="w-4 h-4" />
                  Zum Workbook
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Services */}
        <section className="py-16 md:py-24">
          <div className="max-w-5xl mx-auto px-6">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
              Leistungen rund um NIS-2
            </h2>
            <p className="text-muted-foreground mb-12 max-w-2xl">
              Vier klare Bausteine, die aufeinander aufbauen, aber auch einzeln
              wirken. Sie können pragmatisch mit dem Workbook starten oder direkt
              in ein individuelles Assessment gehen.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {services.map(({ Icon, title, text }) => (
                <div
                  key={title}
                  className="p-6 rounded-2xl border border-border bg-card hover:shadow-lg transition-shadow"
                >
                  <div className="w-12 h-12 rounded-xl bg-accent/10 text-accent flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-xl font-heading font-semibold mb-2">
                    {title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">{text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Scope / Fragen */}
        <section className="py-16 md:py-24 bg-muted/40">
          <div className="max-w-5xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
                Diese Fragen klären wir gemeinsam
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                Statt einer generischen Compliance-Checkliste starten wir mit
                den Fragen, die im Betrieb wirklich zählen, für Gebäudeautomation,
                OT und die Schnittstelle zur IT.
              </p>
            </div>
            <ul className="space-y-3">
              {scope.map((s) => (
                <li key={s} className="flex gap-3">
                  <CheckCircle2 className="w-5 h-5 text-accent flex-shrink-0 mt-1" />
                  <span className="text-foreground/90">{s}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 md:py-24">
          <div className="max-w-3xl mx-auto px-6">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-8">
              Häufige Fragen zu NIS-2
            </h2>
            <p className="text-muted-foreground mb-8">
              Sie betreiben ein Krankenhaus? Details zu KRITIS, NIS-2 und Gebäudeautomation
              im Klinikbetrieb finden Sie auf der Seite{" "}
              <Link to="/ot-security-krankenhaus" className="text-accent font-semibold underline underline-offset-4">
                OT-Security für Krankenhäuser
              </Link>
              .
            </p>
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`}>
                  <AccordionTrigger className="text-left font-semibold">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 md:py-24 bg-primary text-primary-foreground">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
              Bereit für den nächsten Schritt?
            </h2>
            <p className="text-primary-foreground/85 mb-8 leading-relaxed">
              Starten Sie pragmatisch mit dem Workbook oder buchen Sie direkt ein
              Assessment. In beiden Fällen bekommen Sie einen priorisierten
              Fahrplan statt einer weiteren Excel-Liste.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link
                to="/workbook"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-accent text-accent-foreground font-semibold hover:bg-accent/90 transition-colors"
              >
                <BookOpen className="w-4 h-4" />
                Workbook ansehen
              </Link>
              <button
                onClick={openContact}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-primary-foreground/30 font-semibold hover:bg-primary-foreground/10 transition-colors"
              >
                <Mail className="w-4 h-4" />
                Sparring anfragen
              </button>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default Nis2;